import type * as recommendationTypes from 'src/recommendations/types'
import fetch from './fetch'
import { isNilOrEmpty, isValidRE2RegexString } from 'shared/utils'
import type { RecommendationParamType, RecommendationMetadata } from 'shared/assetsBucket/recommendations'
import { type ExtendedPlatforms } from './accounts'

const client = fetch()

export const paths = {
  recommendationsRules: '/recommendations_rules',
  recommendationsRulesStatuses: '/recommendations_rules_statuses'
}

interface RecommendationRuleConfigData {
  name?: string
  severity?: recommendationTypes.SeverityTypes
  recommendation?: string
  params?: recommendationTypes.RecommendationParamsValues
}

interface RecommendationRuleConfig extends RecommendationRuleConfigData {
  id: number
  clientId: number
  ruleId: string
  platform: ExtendedPlatforms
}

export interface RecommendationRuleConfigResponse extends RecommendationRuleConfig {
  createdBy: number
  createdAt: string
  updatedBy: number
  updatedAt: string
  status: 'active' | 'deleted'
}

export interface RecommendationRuleConfigRequest extends RecommendationRuleConfigData {
  clientId: number
  ruleId: string
  platform: ExtendedPlatforms
}

interface RecommendationsRulesStatusData {
  ignored: boolean
  ongoing: boolean
  ignoredUntil?: string
  assignee?: number
}

export interface RecommendationsRulesStatus extends RecommendationsRulesStatusData {
  id: number
  clientId: number
  ruleId: string
}

export interface RecommendationRuleStatusResponse extends RecommendationsRulesStatus {
  createdBy: number
  createdAt: string
  updatedBy: number
  updatedAt: string
  clientId: number
  status: 'active' | 'deleted'
}

export interface RecommendationRuleStatusRequest extends RecommendationsRulesStatusData {
  clientId: number
  ruleId: string
}

// Smart number parser that returns integer if no decimal places, otherwise float
const parseNumber = (value: string): number => {
  const num = Number(value)
  return Number.isInteger(num) ? parseInt(value, 10) : num
}

export const RecommendationParamTypeFormToAPI: Record<RecommendationParamType, (value: string) => string | number> = {
  string: String,
  regex: String,
  percentage: parseNumber,
  number: parseFloat,
  days: parseInt,
  integer: parseInt
} as const

export const RecommendationParamTypeFormValidate: Record<RecommendationParamType, (value: string) => boolean> = {
  string: (value: string) => !isNilOrEmpty(value),
  regex: (value: string) => !isNilOrEmpty(value) && isValidRE2RegexString(value),
  percentage: (value: string) => !isNilOrEmpty(value) && !isNaN(Number(value)) && !isNaN(parseNumber(value)),
  number: (value: string) => !isNilOrEmpty(value) && !isNaN(Number(value)) && !isNaN(parseNumber(value)),
  days: (value: string) => Number.isInteger(parseNumber(value)),
  integer: (value: string) => Number.isInteger(parseNumber(value))
} as const

export type RecommendationsRulesMetadata = Record<string, RecommendationMetadata>

export const getRecommendationsRules = async (clientId: number): Promise<RecommendationRuleConfigResponse[]> => {
  return await client.get<RecommendationRuleConfigResponse[]>({
    path: `${paths.recommendationsRules}?client_id=${clientId}`
  })
}

export const getRecommendationRulesByConfigId = async (
  configId: number,
  clientId: number
): Promise<RecommendationRuleConfigResponse[]> => {
  return await client.get<RecommendationRuleConfigResponse[]>({
    path: `${paths.recommendationsRules}/${configId}?client_id=${clientId}`
  })
}

export const createRecommendationRules = async (
  data: RecommendationRuleConfigRequest
): Promise<RecommendationRuleConfigResponse> => {
  return await client.post<RecommendationRuleConfigResponse>({
    path: paths.recommendationsRules,
    data
  })
}

export const updateRecommendationRules = async ({
  data,
  configId,
  clientId
}: {
  data: RecommendationRuleConfigRequest
  configId: number
  clientId: number
}): Promise<RecommendationRuleConfigResponse> => {
  return await client.put<RecommendationRuleConfigResponse>({
    path: `${paths.recommendationsRules}/${configId}?client_id=${clientId}`,
    data
  })
}

export const deleteRecommendationRules = async ({
  configId,
  clientId
}: {
  configId: number
  clientId: number
}): Promise<RecommendationRuleConfigResponse> => {
  return await client.del<RecommendationRuleConfigResponse>({
    path: `${paths.recommendationsRules}/${configId}?client_id=${clientId}`
  })
}

export const getRecommendationsRulesStatuses = async (
  clientId: number
): Promise<RecommendationRuleStatusResponse[]> => {
  return await client.get<RecommendationRuleStatusResponse[]>({
    path: `${paths.recommendationsRulesStatuses}?client_id=${clientId}`
  })
}

export const getRecommendationRuleStatusByConfigId = async (
  configId: number,
  clientId: number
): Promise<RecommendationRuleStatusResponse[]> => {
  return await client.get<RecommendationRuleStatusResponse[]>({
    path: `${paths.recommendationsRulesStatuses}/${configId}?client_id=${clientId}`
  })
}

export const createRecommendationsRulesStatuses = async (
  data: RecommendationRuleStatusRequest
): Promise<RecommendationRuleStatusResponse> => {
  return await client.post<RecommendationRuleStatusResponse>({
    path: paths.recommendationsRulesStatuses,
    data
  })
}

export const updateRecommendationsRulesStatuses = async ({
  data,
  statusId,
  clientId
}: {
  data: RecommendationRuleStatusRequest
  statusId: number
  clientId: number
}): Promise<RecommendationRuleStatusResponse> => {
  return await client.put<RecommendationRuleStatusResponse>({
    path: `${paths.recommendationsRulesStatuses}/${statusId}?client_id=${clientId}`,
    data
  })
}

export const deleteRecommendationsRulesStatuses = async ({
  statusId,
  clientId
}: {
  statusId: number
  clientId: number
}): Promise<RecommendationRuleStatusResponse> => {
  return await client.del<RecommendationRuleStatusResponse>({
    path: `${paths.recommendationsRulesStatuses}/${statusId}?client_id=${clientId}`
  })
}
